import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  UIEvent,
} from "react";
import { useGeActiveVendorsMutation } from "../../api/api";
import { MdLocationPin } from "react-icons/md";
import { FaToolbox } from "react-icons/fa";

interface Vendors {
  id: number;
  company: string;
  vendorImage: string;
  serviceTypeName: string;
  cityName: string;
}

const OurAvtiveVendor = () => {
  const [getVendors, { isLoading: vendorsLoading }] =
    useGeActiveVendorsMutation();
  const [Allvendors, setAllVendors] = useState<Vendors[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(30);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const fetchVendors = useCallback(
    async (page: number) => {
      try {
        setIsLoadingMore(true);
        const result: any = await getVendors({
          getVendorRequestDto: {
            getTotalCount: true,
            pageSize,
            pageNumber,
            statusId: 1,
          },
        }).unwrap();

        if (page === 1) {
          setAllVendors(result?.data?.vendors || []);
        } else {
          setAllVendors((prev) => [...prev, ...(result?.data?.vendors || [])]);
        }
        setTotalCount(result?.data?.totalCount || 0);
      } catch (error) {
        console.error("Error fetching vendors:", error);
      } finally {
        setIsLoadingMore(false);
      }
    },
    [getVendors, pageSize]
  );

  useEffect(() => {
    const aboutElement = document.getElementById("topbartopscroll");
    if (aboutElement) {
      aboutElement.scrollIntoView({ behavior: "smooth" });
    }
    fetchVendors(1);
  }, [fetchVendors]);

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;
      const hasMoreData = Allvendors.length < totalCount;

      if (isNearBottom && !isLoadingMore && hasMoreData) {
        setCurrentPage((prev) => prev + 1);
        fetchVendors(currentPage + 1);
      }
    },
    [Allvendors.length, totalCount, isLoadingMore, currentPage, fetchVendors]
  );

  const VendorSkeleton = () => (
    <div className="bg-white border border-grayBgColor rounded-2xl p-6 shadow-md">
      <div className="flex flex-col gap-6">
        <div className="w-full h-48 bg-gray-200 animate-pulse rounded-xl" />
        <div className="space-y-4">
          <div className="h-8 bg-gray-200 animate-pulse rounded w-3/4" />
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 bg-gray-200 animate-pulse rounded" />
              <div className="w-16 h-4 bg-gray-200 animate-pulse rounded" />
              <div className="flex-1 h-4 bg-gray-200 animate-pulse rounded" />
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 bg-gray-200 animate-pulse rounded" />
              <div className="w-24 h-4 bg-gray-200 animate-pulse rounded" />
              <div className="flex-1 h-4 bg-gray-200 animate-pulse rounded" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="p-4 md:px-[15vw] md:py-[5vh] h-screen overflow-y-auto"
      onScroll={handleScroll}
      ref={containerRef}
    >
      {/* Header Section */}
      <div className="mb-12">
        <div>
          {/* <p className="flex items-start text-[1.65vh] text-customGrayBase">
        Last modified: 10/22/2024
      </p> */}
          <h1 className="mt-[4%] font-helveticaBold text-[5.5vh] xxl:text-[7.5vh] leading-none text-black">
            Our Trusted Vendors
          </h1>
        </div>
        <p className="text-lg mt-4">
          Explore our curated list of vendor specializing in African groceries
          and catering services within various regions.
        </p>
      </div>

      {/* Supplier Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {vendorsLoading && Allvendors.length === 0 ? (
          // Initial loading
          [...Array(6)].map((_, index) => <VendorSkeleton key={index} />)
        ) : (
          <>
            {Allvendors.map((vendor, index) => (
              <div
                key={index}
                className="bg-white border border-grayBgColor rounded-2xl p-6 shadow-md hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] cursor-pointer"
              >
                <div className="flex flex-col gap-6">
                  <div className="w-full h-48  rounded-xl flex items-center justify-center p-4">
                    {vendor.vendorImage ? (
                      <img
                        src={vendor.vendorImage}
                        alt={vendor.company}
                        className="w-full h-full object-contain rounded-lg"
                        onError={(e) => {
                          e.currentTarget.src = "/storeDummyImg.png";
                        }}
                      />
                    ) : (
                      <img
                        src="/storeDummyImg.png"
                        alt="storeDummyImg"
                        className="w-full h-full object-contain"
                      />
                    )}
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-2xl font-helveticaBold text-customGrayDark line-clamp-1 hover:line-clamp-none">
                      {vendor.company}
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-customBlackColor">
                        <MdLocationPin size={20} />
                        <span className="font-helveticaMedium">Region:</span>
                        <span className="text-customGrayBase flex-1">
                          {vendor.cityName}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-customBlackColor">
                        <FaToolbox size={20} />
                        <span className="font-helveticaMedium">
                          Specialized in:
                        </span>
                        <span className="text-customGrayBase flex-1">
                          {vendor.serviceTypeName}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Show loading more skeleton */}
            {isLoadingMore &&
              [...Array(3)].map((_, index) => (
                <VendorSkeleton key={`loading-more-${index}`} />
              ))}

            {/* No vendors message */}
            {!vendorsLoading && Allvendors.length === 0 && (
              <div className="col-span-3 text-center py-16 bg-white rounded-xl shadow-lg">
                <p className="text-xl text-gray-600">
                  No vendors available at this time.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OurAvtiveVendor;
